import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Form } from '@/components/ui/form';
import RoleAssignmentForm, { RoleAssignment } from './RoleAssignmentForm';
import { Role } from '../../hooks/useFetchRoles';
import { API } from '@/utils/API';

// Define the form schema
const roleAssignmentSchema = z.object({
  role_key: z.string().min(1, 'Role is required'),
  resource_type: z.string().optional(),
  resource_id: z.string().optional(),
  resource_label: z.string().optional(),
});

const invitationFormSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  roleAssignments: z
    .array(roleAssignmentSchema)
    .min(1, 'At least one role assignment is required'),
});

type InvitationFormData = z.infer<typeof invitationFormSchema>;

const NewInvitationForm: React.FC<{ roles: Record<string, Role> }> = ({
  roles,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [submissionSuccess, setSubmissionSuccess] = useState('');
  const [showAddAssignmentForm, setShowAddAssignmentForm] = useState(false);

  const form = useForm<InvitationFormData>({
    resolver: zodResolver(invitationFormSchema),
    defaultValues: {
      email: '',
      roleAssignments: [],
    },
    mode: 'onChange',
  });

  const { append, remove } = useFieldArray({
    control: form.control,
    name: 'roleAssignments',
  });

  const onSubmit = async (data: InvitationFormData) => {
    setIsSubmitting(true);
    setSubmissionError('');
    setSubmissionSuccess('');

    try {
      const response = await API.authenticated.admin.fetch('/invitations', {
        method: 'POST',
        body: JSON.stringify({
          invitation: {
            ...data,
            role_assignments: data.roleAssignments.map(assignment => ({
              role_key: assignment.role_key,
              resource_type: assignment.resource_type,
              resource_id: assignment.resource_id,
            })),
          },
        }),
      });
      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to create invitation');
      }
      setSubmissionSuccess(`Invitation sent to ${data.email}`);
      form.reset();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An unexpected error occurred';
      setSubmissionError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleAssignments = form.watch('roleAssignments');

  const onAddAssignment = (assignment: RoleAssignment) => {
    const isDuplicate = roleAssignments.some(
      existing =>
        existing.role_key === assignment.role_key &&
        existing.resource_type === assignment.resource_type &&
        existing.resource_id === assignment.resource_id
    );

    if (!isDuplicate) {
      append(assignment);
    }
    setShowAddAssignmentForm(false);
  };

  if (showAddAssignmentForm) {
    return (
      <RoleAssignmentForm
        onSubmit={onAddAssignment}
        onCancel={() => setShowAddAssignmentForm(false)}
        roles={roles}
      />
    );
  }

  return (
    <Form {...form}>
      <div className="space-y-2">
        <Label htmlFor="email" className="text-sm font-medium text-gray-700">
          Email
        </Label>
        <Input
          id="email"
          type="email"
          placeholder="Enter email address"
          {...form.register('email')}
          className="w-full"
          disabled={isSubmitting}
        />
        {form.formState.errors.email && (
          <p className="text-red-500 text-sm">
            {form.formState.errors.email.message}
          </p>
        )}
      </div>

      {/* Role Assignments Table */}
      <div className="mt-16">
        <div className="flex justify-end mb-4">
          <Button onClick={() => setShowAddAssignmentForm(true)}>
            Add Role Assignment
          </Button>
        </div>
        <div className="rounded-lg border border-gray-200">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  Name
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  Role Assignment
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  Selection(s)
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {roleAssignments.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No role assignments added yet
                  </td>
                </tr>
              ) : (
                roleAssignments.map((result, index) => (
                  <tr
                    key={`${result.role_key}-${result.resource_type}-${result.resource_id}-${index}`}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {roles[result.role_key]?.name}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {result.resource_type}
                    </td>

                    <td className="text-wrap px-6 py-4 text-sm text-gray-500">
                      {result.resource_label}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-500">
                      <button
                        className="hover:text-red-400 cursor-pointer"
                        aria-label={`Remove role assignment for ${roles[result.role_key]?.name}`}
                        onClick={() => remove(index)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Button
        type="button"
        className="mt-10 w-full"
        onClick={form.handleSubmit(onSubmit)}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Sending...' : 'Create Invitation'}
      </Button>

      {form.formState.errors.roleAssignments && (
        <p className="text-red-500 text-sm text-center mt-10">
          {form.formState.errors.roleAssignments.message}
        </p>
      )}

      {submissionSuccess && (
        <p className="text-green-500 text-center">{submissionSuccess}</p>
      )}
      {submissionError && (
        <p className="text-red-500 text-center">{submissionError}</p>
      )}
    </Form>
  );
};

export default NewInvitationForm;
